import React, { useEffect, useRef } from "react";
import { Info, X } from "lucide-react";
import "./SecondQuestionReminder.css";

/**
 * Shown once, before the visitor spends their last free question of the day.
 *
 * Not a limit warning — that arrives after the fact and there is nothing to do
 * about it. This lands while the question is still editable, which is the only
 * moment the advice is worth anything.
 *
 * Deliberately not dismissible into a submission: closing it or "Go Back and
 * Review" both return to the question without sending it. The only path that
 * submits is the button that says so.
 */
export default function SecondQuestionReminder({ limit, onReview, onSubmit }) {
  const dialogRef = useRef(null);
  const submitRef = useRef(null);

  useEffect(() => {
    // The submit button is the primary action, but focus lands on the dialog
    // itself so a screen reader reads the reason before the choice, and a
    // stray Enter does not send the question the visitor has not re-read.
    dialogRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") onReview();
      if (e.key !== "Tab") return;
      // Keep focus inside the dialog while it is open.
      const focusable = dialogRef.current?.querySelectorAll(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
      );
      if (!focusable || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onReview]);

  return (
    <div
      className="sqr-backdrop"
      // A click that starts inside the dialog and ends on the backdrop is a
      // drag, not a dismissal.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onReview();
      }}
    >
      <div
        className="sqr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sqr-title"
        aria-describedby="sqr-body"
        tabIndex={-1}
        ref={dialogRef}
      >
        <button
          type="button"
          className="sqr-close"
          aria-label="Close and go back to my question"
          onClick={onReview}
        >
          <X size={18} aria-hidden="true" />
        </button>

        <div className="sqr-badge" aria-hidden="true">
          <Info size={22} />
        </div>

        <h2 id="sqr-title">Make Your Questions Count</h2>
        <p id="sqr-body">
          To help keep LawFusion free, we limit legal questions to {limit} per
          24-hour period.
        </p>

        <div className="sqr-details">
          Please make sure this question includes the important facts, dates,
          amounts, documents, and other details that could affect the answer.{" "}
          <strong>
            The more complete your question, the more useful your analysis can
            be.
          </strong>
        </div>

        <div className="sqr-actions">
          <button type="button" className="sqr-btn sqr-btn-secondary" onClick={onReview}>
            Go Back and Review
          </button>
          <button
            type="button"
            className="sqr-btn sqr-btn-primary"
            onClick={onSubmit}
            ref={submitRef}
          >
            Submit My Question
          </button>
        </div>
      </div>
    </div>
  );
}
